import { Alert } from 'antd';
import { NSPButton } from '@components/ui/NSPButton';
import { isActiveSendJob, type VendorInvoiceSendSummary } from '@hooks/graphql/useVendorInvoiceSendJobs';
import type { VendorInvoiceSendJob } from '@custom-types/invoices';

// "2026-08" -> "Aug 2026"
const formatMonthYear = (monthYear: string) => {
  const [year, month] = monthYear.split('-').map(Number);
  if (!year || !month) return monthYear;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const FINISHED_TITLES: Record<VendorInvoiceSendSummary['type'], string> = {
  success: 'Vendor invoices sent',
  warning: 'Vendor invoices sent — with warnings',
  error: 'Some vendor invoices were not sent',
};

type VendorInvoiceSendBannerProps = {
  job: VendorInvoiceSendJob;
  summary: VendorInvoiceSendSummary;
  // The ZIP can only be built while the dashboard shows the send's month
  canDownloadZip: boolean;
  onDownloadZip: (job: VendorInvoiceSendJob) => void;
  onDismiss: (jobId: number) => void;
};

// Progress and result of one background vendor invoice send. Shown until the user closes it —
// it comes back after a refresh or a new login until then.
const VendorInvoiceSendBanner = ({ job, summary, canDownloadZip, onDownloadZip, onDismiss }: VendorInvoiceSendBannerProps) => {
  const month = formatMonthYear(job.monthYear);

  if (isActiveSendJob(job)) {
    const processed = job.rows.filter((row) => row.status !== 'PENDING').length;
    return (
      <Alert
        message={`Vendor invoices for ${month} are being sent`}
        description={`${processed} of ${job.invoiceCount} vendor invoice(s) processed.`}
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
        data-testid="vendor-invoice-send-banner"
        data-status={job.status}
      />
    );
  }

  return (
    <Alert
      message={`${FINISHED_TITLES[summary.type]} (${month})`}
      description={(
        <>
          <div>{summary.message}</div>
          {summary.emailedCount > 0 && (
            canDownloadZip ? (
              <NSPButton
                size="small"
                style={{ marginTop: 8 }}
                onClick={() => onDownloadZip(job)}
                data-testid="vendor-invoice-send-download-zip"
              >
                Download sent invoices (ZIP)
              </NSPButton>
            ) : (
              <div style={{ marginTop: 8 }}>{`Open ${month} to download the sent invoices as a ZIP.`}</div>
            )
          )}
        </>
      )}
      type={summary.type}
      showIcon
      closable
      onClose={() => onDismiss(job.jobId)}
      style={{ marginBottom: '16px' }}
      data-testid="vendor-invoice-send-banner"
      data-status={job.status}
    />
  );
};

export default VendorInvoiceSendBanner;
